import {Box, useTheme} from '@mui/material';
import {Formik, FormikConfig, FormikProps} from 'formik';
import {FC, useEffect, useMemo, useState} from 'react';
import {useIntl} from 'react-intl';
import {companyApi, locationsApi, usersApi} from 'src/api';
import {useAppDispatch} from 'src/app/hooks';
import {AMPAutocomplete} from 'src/components/common/AMPAutocomplete';
import {AMPAutocompleteProps} from 'src/components/common/AMPAutocomplete/AMPAutocomplete.types';
import {AMPInput} from 'src/components/common/AMPInput';
import {CancelButton} from 'src/components/common/CancelButton';
import {SubmitButton} from 'src/components/common/SubmitButton';
import {TableFilterFormTypes} from 'src/components/reports/TableFilter/TableFilter.types';
import {preloaderChangeStatus} from 'src/stores/common/commonSlice';
import {AppDispatch, BaseUserInfo} from 'src/types';
import {getHelperText} from 'src/utils/forms';
import {findAutocompleteValueById, isErrorObject, valuesTrimmer} from 'src/utils/helper';
import {NotifyError} from 'src/utils/notification';
import {
    addClientToLocationFormSchema,
    AddClientToLocationFormType,
} from '../../forms/formData/addClientToLocationForm';
import {addClientToLocationFormValues} from '../../forms/formData/common/initialData';
import {AddClientToLocationFormProps} from './AddClientToLocationForm.types';
import {UserTypeEnum} from 'src/enums/common';

type AddUserToLocationMutationTrigger = ReturnType<
    typeof locationsApi.useAddUserToLocationMutation
>[0];
type AddCompanyToLocationMutationTrigger = ReturnType<
    typeof companyApi.useAddCompanyToLocationMutation
>[0];

const handleSubmit =
    (
        trigger: AddUserToLocationMutationTrigger,
        addCompanyTrigger: AddCompanyToLocationMutationTrigger,
        dispatch: AppDispatch,
        locationId: number,
        businessId: number,
        handleClose: () => void,
        users?: BaseUserInfo[],
        refetch?: () => void
    ): FormikConfig<AddClientToLocationFormType>['onSubmit'] =>
        async (value) => {
            const values = valuesTrimmer(value);

            const currentUser = users && users.find((user) => user.id === +values.userId);

            const parkingPlaceValue = () => {
                if (values.parkingPlace) {
                    return values.parkingPlace;
                }
                return currentUser?.parkingAddress?.parkingPlace ?? 0;
            };

            const data = {
                businessId,
                locationId,
                userId: +values.userId,
                parkingPlace: String(parkingPlaceValue()),
            };
            try {
                dispatch(preloaderChangeStatus(true));

                if (!currentUser?.company) {
                    await trigger(data).unwrap();

                    return;
                }

                await addCompanyTrigger({
                    companyId: currentUser?.company?.id ?? 0,
                    data: {
                        businessId,
                        locationId,
                    },
                }).unwrap();
            } catch (error) {
                if (isErrorObject(error)) {
                    NotifyError(error.data.message);
                }
            } finally {
                if (refetch) {
                    refetch();
                }

                handleClose();

                dispatch(preloaderChangeStatus(false));
            }
        };

export const AddClientToLocationForm: FC<AddClientToLocationFormProps> = ({
                                                                              existClients,
                                                                              businessId,
                                                                              locationId,
                                                                              handleClose,
                                                                          }) => {
    const intl = useIntl();

    const theme = useTheme();

    const dispatch = useAppDispatch();

    const [trigger] = locationsApi.useAddUserToLocationMutation();
    const [addCompanyTrigger] = companyApi.useAddCompanyToLocationMutation();
    const [getClients] = usersApi.useLazyGetUsersQuery();
    const {refetch} = locationsApi.useGetLocationByIdQuery({
        locationId,
        businessId,
    });

    const [allClients, setClients] = useState<Array<BaseUserInfo>>([]);

    const getClientsData = async () => {
        const driverClients = await getClients({
            data: {businessId, userTypes: [UserTypeEnum.PRIVATE_DRIVER], locationExists: false},
            params: {size: 2000},
        });

        const companyClients = await getClients({
            data: {businessId, userTypes: [UserTypeEnum.COMPANY]},
            params: {size: 2000},
        });

        setClients((prevState) => {
            const result = [...prevState];

            (driverClients?.data?.content ?? []).forEach((client) => {
                if (result.findIndex((item) => item.id === client.id) === -1) {
                    result.push(client);
                }
            });

            (companyClients?.data?.content ?? []).forEach((client) => {
                if (result.findIndex((item) => item.id === client.id) === -1) {
                    result.push(client);
                }
            });

            return result;
        });
    };

    useEffect(() => {
        getClientsData();
    }, []);

    const processedClients = useMemo(() => {
        if (!allClients.length) {
            return [];
        }

        const data = allClients
            .filter((client) => !client.chargePointId)
            .filter((client) => {
                return (
                    existClients.findIndex((item) => {
                        if (client.id === item?.id) {
                            return true;
                        }

                        if (
                            'user' in item &&
                            typeof item['user'] === 'object' &&
                            item['user'] &&
                            'id' in item['user']
                        ) {
                            return client.id === item['user']['id'];
                        }
                    }) === -1
                );
            });

        return data.map((client) => {
            let label = '';

            // For drivers: show name if available, otherwise show email
            if (client?.firstName && client?.lastName) {
                label = `${client.firstName} ${client.lastName}`;
            } else if (client?.emailAddress && !client?.company) {
                // This is a driver without name, show email
                label = client.emailAddress;
            }

            // For companies: show company name instead of contact person
            if (client?.company?.name) {
                label = client.company.name;
            }

            return {
                id: client.id,
                label,
                searchText: `${label} ${client?.emailAddress || ''} ${client?.firstName || ''} ${client?.lastName || ''} ${client?.company?.name || ''}`.toLowerCase(),
            };
        });
    }, [allClients]);

    const handleChangeClientValue =
        (
            setFieldValue: FormikProps<TableFilterFormTypes>['setFieldValue'],
            fieldName: string,
            values: AddClientToLocationFormType
        ): AMPAutocompleteProps['onChange'] =>
            (_e, newValue) => {
                if (newValue) {
                    setFieldValue(fieldName, newValue.id);
                    const client = allClients.find((item) => item.id === newValue.id);
                    values['parkingPlace'] = client?.parkingAddress?.parkingPlace ?? '';
                }
            };

    return (
        <Formik
            initialValues={addClientToLocationFormValues}
            validationSchema={addClientToLocationFormSchema}
            onSubmit={handleSubmit(
                trigger,
                addCompanyTrigger,
                dispatch,
                locationId,
                businessId,
                handleClose,
                allClients,
                refetch
            )}
        >
            {({
                  errors,
                  handleBlur,
                  handleChange,
                  handleSubmit,
                  setFieldValue,
                  touched,
                  values,
              }: FormikProps<AddClientToLocationFormType>) => (
                <form
                    onSubmit={handleSubmit}
                    noValidate
                >
                    <AMPAutocomplete
                        options={processedClients}
                        value={findAutocompleteValueById(processedClients, +values['userId'])}
                        label={'client'}
                        input={'userId'}
                        showAvatar
                        onChange={handleChangeClientValue(setFieldValue, 'userId', values)}
                        onBlur={handleBlur}
                        description="selectDriverDescription"
                        hasError={touched['userId'] && Boolean(errors['userId'])}
                        helperText={getHelperText('userId', touched, errors, intl)}
                    />
                    <AMPInput
                        value={values['parkingPlace']}
                        type={'text'}
                        label={'parkingPlace'}
                        input={'parkingPlace'}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        hasError={touched['parkingPlace'] && Boolean(errors['parkingPlace'])}
                        helperText={getHelperText('parkingPlace', touched, errors, intl)}
                    />
                    <Box
                        sx={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            marginTop: theme.spacing(20 / 8),
                        }}
                    >
                        <CancelButton
                            text="cancel"
                            handler={handleClose}
                        />

                        <SubmitButton text="submit"/>
                    </Box>
                </form>
            )}
        </Formik>
    );
};
